/* Search and the Bag as modals. The client, 2026-09-30: "search should be a modal / bag should
   be a modal too".

   The markup (snippets/lv-overlays.liquid, rendered from the layout) ships both as native
   <dialog> elements, closed. This script is the only thing that opens them: every trigger in
   the chrome and the quick bar remains a real anchor to /search or /cart, and a click is
   intercepted only when a dialog can actually open — so no JavaScript, or a browser without
   <dialog>, changes nothing at all.

   Search asks /search/suggest.json (products, articles, pages) 180ms after the typing stops;
   answers are cached per term and stale ones are dropped by a sequence number. Enter is the
   real form: it submits to the results page, which remains the floor.

   The bag is rendered from /cart.js on open and after every change, so the file here is the
   only renderer — the page's own /cart stays the no-JS floor. Quantity buttons and Remove go
   through /cart/change.js; every count in the chrome (and the quick bar's label) is rewritten
   from the cart the store answers with. An add-to-bag form anywhere on the site (the product's
   two forms, the buy bar) is answered over fetch and opens the modal; any failure falls back
   to a native form submit, exactly as if this script were not here. */
(() => {
  const dialogs = new Map(
    [...document.querySelectorAll('dialog[data-overlay]')].map((d) => [d.getAttribute('data-overlay'), d]),
  );
  const bag = dialogs.get('bag');
  if (!dialogs.size || typeof HTMLDialogElement === 'undefined' || !bag?.showModal) return;

  const root = document.documentElement;
  const rootPath = window.Shopify?.routes?.root ?? '/';

  /* Money the way the shop's own pages print it: no trailing zeros ($600, not $600.00). The
     currency is whatever the storefront's currency is; the fallback only fires off-storefront. */
  const money = (cents) => {
    const whole = cents % 100 === 0;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active ?? 'USD',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(whole ? 0 : 2)}`;
    }
  };
  /* A CDN URL at the size the layout draws, keeping any ?v= the store already carries. */
  const sized = (url, width) => {
    if (!url) return null;
    const src = url.startsWith('//') ? `https:${url}` : url;
    return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
  };

  const open = (name, opts = {}) => {
    const dialog = dialogs.get(name);
    if (!dialog || dialog.open) return Boolean(dialog);
    dialog.showModal();
    /* The bag drawer's slide-in — see the shell's comment for the whole story (a keyframe
       painted wrongly in WebKit; a bare [open] transition cannot start from the display:none a
       dialog opens out of). The forced reflow commits the off-screen frame, then the class
       flips it in and the transition runs. One tick, no frames guessed. */
    if (name === 'bag') {
      void dialog.offsetWidth;
      dialog.classList.add('v2-overlay-in');
    }
    root.classList.add('v2-overlay-open');
    if (name === 'search') dialog.querySelector('[data-overlay-input]')?.focus();
    /* The platform focuses the first focusable control on showModal — the close X — which then
       wore the browser's own focus ring (the client: "when i press account, the X is
       highlighted"). The panel is the focus target instead: same trap, same Escape, no ring. */
    else dialog.querySelector('.v2-modal__panel')?.focus({ preventScroll: true });
    if (name === 'bag' && opts.refresh !== false) refreshCart();
    return true;
  };

  /* The triggers, the close buttons, the backdrop. A click on the ::backdrop arrives with the
     dialog itself as the target, which is exactly what distinguishes it from a click inside the
     panel. The 'close' event catches every exit — button, backdrop, Escape — and hands the page
     back. */
  document.addEventListener('click', (event) => {
    const closer = event.target.closest?.('[data-overlay-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      /* A closer may also be an opener — the bag's empty state hands to the account modal
         (the reference the client liked puts "Login or create an account" in the bag), and
         the hand-off must not leave two dialogs stacked. */
      const next = closer.getAttribute('data-overlay-open');
      if (next) open(next);
      return;
    }
    const trigger = event.target.closest?.('[data-overlay-open]');
    if (!trigger) return;
    if (open(trigger.getAttribute('data-overlay-open'))) event.preventDefault();
  });
  for (const dialog of dialogs.values()) {
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  }
  document.addEventListener('close', (event) => {
    if (event.target.matches?.('.v2-modal') && ![...dialogs.values()].some((d) => d.open)) {
      root.classList.remove('v2-overlay-open');
    }
    /* The drawer re-arms: without this the next open would start at translateX(0) and skip its
       slide (the class from the previous open would still be on the dialog). */
    event.target.classList?.remove('v2-overlay-in');
  }, true);
  /* Escape closes the overlay even with text in the field. The native dialog would close too —
     except that the first Escape in a focused search input is spent clearing it (Chromium), so
     a reader who typed something would press a key that only empties their own box. One press,
     always out. */
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    for (const dialog of dialogs.values()) {
      if (dialog.open) {
        event.preventDefault();
        dialog.close();
      }
    }
  });

  /* ── search ─────────────────────────────────────────────────────────────────────────────── */
  const search = dialogs.get('search');
  if (search) {
    const form = search.querySelector('form');
    const input = search.querySelector('[data-overlay-input]');
    const idle = search.querySelector('[data-overlay-idle]');
    const results = search.querySelector('[data-overlay-results]');
    let seq = 0;
    let timer = 0;
    const cache = new Map();

    const row = (title, href, opts = {}) => {
      const a = document.createElement('a');
      a.className = 'v2-modal__row';
      a.href = href;
      if (opts.image) {
        const thumb = document.createElement('span');
        thumb.className = 'v2-modal__thumb';
        const img = document.createElement('img');
        img.src = opts.image;
        img.alt = '';
        img.width = 44;
        img.height = 55;
        thumb.append(img);
        a.append(thumb);
      }
      const name = document.createElement('span');
      name.className = 'v2-modal__rowname';
      name.textContent = title;
      a.append(name);
      if (opts.tag) {
        const tag = document.createElement('span');
        tag.className = 'v2-modal__rowtag';
        tag.textContent = opts.tag;
        a.append(tag);
      }
      if (opts.price) {
        const price = document.createElement('span');
        price.className = 'v2-modal__rowprice';
        price.textContent = opts.price;
        a.append(price);
      }
      return a;
    };
    const group = (label, rows) => {
      const wrap = document.createElement('div');
      wrap.className = 'v2-modal__group';
      const head = document.createElement('p');
      head.className = 'v2-label';
      head.textContent = label;
      wrap.append(head);
      wrap.append(...rows);
      return wrap;
    };

    const render = (term, data) => {
      results.textContent = '';
      const found = data?.resources?.results ?? {};
      const products = found.products ?? [];
      const articles = found.articles ?? [];
      const pages = found.pages ?? [];
      if (!products.length && !articles.length && !pages.length) {
        const none = document.createElement('p');
        none.className = 'v2-small';
        none.textContent = `Nothing matches \u201c${term}\u201d yet.`;
        results.append(none);
      } else {
        if (products.length) {
          results.append(
            group(
              'Objects',
              products.map((p) =>
                row(p.title, p.url, {
                  image: sized(p.featured_image?.url ?? p.image?.url, 120),
                  price: typeof p.price === 'number' ? money(p.price) : null,
                }),
              ),
            ),
          );
        }
        if (articles.length) results.append(group('Vacanza Club', articles.map((a) => row(a.title, a.url, { tag: 'Journal' }))));
        if (pages.length) results.append(group('The house', pages.map((p) => row(p.title, p.url, { tag: 'Page' }))));
      }
      const more = document.createElement('p');
      more.className = 'v2-modal__more';
      const all = document.createElement('a');
      all.className = 'v2-link';
      all.href = `${form.action}?q=${encodeURIComponent(term)}`;
      all.textContent = `All results for \u201c${term}\u201d`;
      more.append(all);
      results.append(more);
    };

    const lookup = (term) => {
      if (!cache.has(term)) {
        cache.set(
          term,
          fetch(
            `${rootPath}search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product,article,page&resources[limit]=6`,
            { headers: { Accept: 'application/json' } },
          )
            .then((res) => (res.ok ? res.json() : null))
            .catch(() => null),
        );
      }
      return cache.get(term);
    };

    const run = async () => {
      const term = input.value.trim();
      const id = ++seq;
      if (term.length < 2) {
        results.textContent = '';
        idle.hidden = false;
        return;
      }
      idle.hidden = true;
      const data = await lookup(term);
      if (seq !== id) return; // a newer keystroke owns the panel now
      render(term, data);
    };

    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(run, 180);
    });
  }

  /* ── the bag ────────────────────────────────────────────────────────────────────────────── */
  const lines = bag.querySelector('[data-overlay-lines]');
  const empty = bag.querySelector('[data-overlay-empty]');
  const foot = bag.querySelector('[data-overlay-foot]');
  const loading = bag.querySelector('[data-overlay-loading]');
  const subtotal = bag.querySelector('[data-overlay-subtotal]');

  const publishCounts = (count) => {
    for (const el of document.querySelectorAll('[data-bag-count]')) el.textContent = count;
    for (const a of document.querySelectorAll('a.v2-quick__btn[data-overlay-open="bag"]')) {
      a.setAttribute('aria-label', `Bag, ${count} item${count === 1 ? '' : 's'}`);
    }
  };

  const line = (item) => {
    const li = document.createElement('li');
    li.className = 'v2-modal__line';

    const frame = document.createElement('a');
    frame.className = 'v2-modal__frame';
    frame.href = item.url;
    frame.tabIndex = -1;
    const image = sized(item.image, 160);
    if (image) {
      const img = document.createElement('img');
      img.src = image;
      img.alt = '';
      img.width = 64;
      img.height = 80;
      frame.append(img);
    }

    const info = document.createElement('div');
    info.className = 'v2-modal__info';
    const name = document.createElement('a');
    name.className = 'v2-modal__name';
    name.href = item.url;
    name.textContent = item.product_title;
    info.append(name);
    const option = (item.options_with_values ?? []).map((o) => o.value).join(' / ');
    if (option) {
      const variant = document.createElement('span');
      variant.className = 'v2-modal__variant';
      variant.textContent = option;
      info.append(variant);
    }

    const meta = document.createElement('div');
    meta.className = 'v2-modal__meta';
    const price = document.createElement('span');
    price.className = 'v2-modal__price';
    price.textContent = money(item.final_line_price);
    const qty = document.createElement('span');
    qty.className = 'v2-modal__qty';
    const few = document.createElement('button');
    few.type = 'button';
    few.className = 'v2-modal__qbtn';
    few.textContent = '\u2212';
    few.setAttribute('aria-label', `One fewer ${item.product_title}`);
    few.dataset.cartId = item.key;
    few.dataset.cartOp = 'down';
    const num = document.createElement('span');
    num.className = 'v2-modal__qnum';
    num.textContent = item.quantity;
    const many = document.createElement('button');
    many.type = 'button';
    many.className = 'v2-modal__qbtn';
    many.textContent = '+';
    many.setAttribute('aria-label', `One more ${item.product_title}`);
    many.dataset.cartId = item.key;
    many.dataset.cartOp = 'up';
    qty.append(few, num, many);
    meta.append(qty, price);
    info.append(meta);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'v2-modal__remove';
    remove.textContent = 'Remove';
    remove.dataset.cartId = item.key;
    remove.dataset.cartOp = 'remove';
    info.append(remove);

    li.append(frame, info);
    return li;
  };

  const renderBag = (cart) => {
    publishCounts(cart.item_count);
    lines.textContent = '';
    if (!cart.items.length) {
      empty.hidden = false;
      foot.hidden = true;
      lines.hidden = true;
      return;
    }
    empty.hidden = true;
    foot.hidden = false;
    lines.hidden = false;
    for (const item of cart.items) lines.append(line(item));
    subtotal.textContent = money(cart.total_price);
  };

  /* Mutations serialize. This store's cart endpoints answer in about two thirds of a second,
     and a second click should COUNT, not bounce off a busy flag: the pads update their number
     the moment they are pressed and the server's cart re-renders every count when it answers,
     so however fast the presses came, the page converges on the truth. */
  let chain = Promise.resolve();
  const queue = (work) => {
    chain = chain.then(work).catch(() => {});
    return chain;
  };

  const refreshCart = () =>
    queue(async () => {
      if (!lines.children.length) loading.hidden = false;
      try {
        const res = await fetch(`${rootPath}cart.js`, { headers: { Accept: 'application/json' } });
        if (res.ok) renderBag(await res.json());
      } finally {
        loading.hidden = true;
      }
    });

  const changeLine = (id, quantity) =>
    queue(async () => {
      let res = null;
      try {
        res = await fetch(`${rootPath}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          // This store's change.js takes the line-item KEY as `id` — the same key survives
          // removals (a key in `line` is rejected there; `line` is the 1-based index only).
          body: JSON.stringify({ id, quantity }),
        });
      } catch {
        /* the refresh below puts the truth back */
      }
      if (res?.ok) {
        renderBag(await res.json());
      } else {
        const fresh = await fetch(`${rootPath}cart.js`, { headers: { Accept: 'application/json' } });
        if (fresh.ok) renderBag(await fresh.json());
      }
    });

  bag.addEventListener('click', (event) => {
    const control = event.target.closest?.('[data-cart-id]');
    if (!control) return;
    event.preventDefault();
    const op = control.dataset.cartOp;
    let quantity = 0;
    if (op !== 'remove') {
      const num = control.closest('.v2-modal__qty')?.querySelector('.v2-modal__qnum');
      const current = Number(num?.textContent ?? '0');
      quantity = Math.max(0, current + (op === 'up' ? 1 : -1));
      if (num) num.textContent = quantity; // optimistic; the cart's answer re-renders the line
    }
    changeLine(control.dataset.cartId, quantity);
  });

  /* Adding opens the bag. The form posts exactly as it always did over fetch; the moment
     anything about that fails, the page's own submit takes over — the customer ends up on the
     cart page like before this script existed, never at a dead button. */
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = form.getAttribute('action') ?? '';
      if (!/\/cart\/add/.test(action)) return;
      event.preventDefault();
      const button = form.querySelector('[type="submit"]');
      button?.setAttribute('disabled', '');
      const url = form.action.endsWith('.js') ? form.action : `${form.action}.js`;
      fetch(url, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) })
        .then((res) => {
          if (!res.ok) throw new Error(String(res.status));
          return res.json();
        })
        .then(() => fetch(`${rootPath}cart.js`, { headers: { Accept: 'application/json' } }))
        .then((res) => res.json())
        .then((cart) => {
          renderBag(cart);
          open('bag', { refresh: false });
        })
        .catch(() => form.submit())
        .finally(() => button?.removeAttribute('disabled'));
    },
    true,
  );
})();
